import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, CheckCircle, MapPin, Clock, Cloud, Sun, CreditCard, Leaf, X, CaretRight, ChartBar, Users, CalendarBlank, WarningCircle, TrendUp, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

// Modern field-services SaaS palette — cool off-white + deep petrol teal + electric lime + coral.
const PAGE = "#F4F5F7";         // cool bone (not beige)
const PAGE_LIGHT = "#FFFFFF";
const INK = "#15191E";           // deep charcoal
const INK_SOFT = "#4A5058";
const TEAL = "#0F766E";          // deep petrol teal — primary
const TEAL_DEEP = "#0B5A54";
const LIME = "#84CC16";          // electric lime — money / paid
const LIME_SOFT = "#ECFCCB";
const CORAL = "#EF4444";         // alert / outstanding
const RULE = "#DDE1E6";

const ROUND_SEED = [
  { id: "r1", customer: "42 Linden Gardens", name: "Mrs Ashworth", service: "Front & back — mow, edge, hedges", price: 45, gateCode: "2671", note: "Dog in the conservatory — leave back gate open", lastVisitDays: 14, status: "due", photoBefore: null, photoAfter: null, paid: false, day: "Thu" },
  { id: "r2", customer: "The Coach House, Willow Lane", name: "Mr Brook", service: "Lawn mow + trim", price: 30, gateCode: null, note: "Side gate is the one with the rose arch", lastVisitDays: 21, status: "due", photoBefore: null, photoAfter: null, paid: false, day: "Thu" },
  { id: "r3", customer: "7 Orchard Rise", name: "Singh family", service: "Hedge cut (annual)", price: 85, gateCode: "0044", note: "Please avoid 2–3pm (baby naps)", lastVisitDays: 365, status: "due", photoBefore: null, photoAfter: null, paid: false, day: "Thu" },
  { id: "r4", customer: "Flat 2, Mill Street", name: "Ms O'Connor", service: "Window clean — exterior", price: 22, gateCode: null, note: "Key under the third stone by the front door", lastVisitDays: 28, status: "due", photoBefore: null, photoAfter: null, paid: false, day: "Thu" },
  { id: "r5", customer: "The Old Rectory", name: "Rev. Palmer", service: "Full garden tidy + leaf-blow", price: 120, gateCode: null, note: "Gravel drive — park on grass if wet", lastVisitDays: 14, status: "done", photoBefore: "snapped", photoAfter: "snapped", paid: true, day: "Thu" },
];

// Week-at-a-glance seed data — a plausible week for a one-person round
const WEEK_DAYS = [
  { day: "Mon", stops: 4, earned: 240, outstanding: 0 },
  { day: "Tue", stops: 5, earned: 285, outstanding: 45 },
  { day: "Wed", stops: 3, earned: 170, outstanding: 0 },
  { day: "Thu", stops: 5, earned: 120, outstanding: 0, today: true }, // today — still in progress
  { day: "Fri", stops: 4, earned: 0, outstanding: 0, upcoming: true },
  { day: "Sat", stops: 2, earned: 0, outstanding: 0, upcoming: true },
];

const CUSTOMERS_SEED = [
  { customer: "42 Linden Gardens", name: "Mrs Ashworth", visits: 22, ltv: 990, lastPaid: "14d", outstanding: 0, cadence: "Every 2 wks" },
  { customer: "The Coach House", name: "Mr Brook", visits: 15, ltv: 450, lastPaid: "21d", outstanding: 30, cadence: "Every 3 wks" },
  { customer: "7 Orchard Rise", name: "Singh family", visits: 3, ltv: 255, lastPaid: "12 mo", outstanding: 85, cadence: "Annual" },
  { customer: "Flat 2, Mill Street", name: "Ms O'Connor", visits: 12, ltv: 264, lastPaid: "28d", outstanding: 0, cadence: "Monthly" },
  { customer: "The Old Rectory", name: "Rev. Palmer", visits: 26, ltv: 3120, lastPaid: "just now", outstanding: 0, cadence: "Fortnightly" },
];

const PROVIDERS = ["SumUp", "Square", "FlatPay"];

const TABS = [
  { key: "round", label: "Today's round", icon: MapPin },
  { key: "week", label: "This week", icon: ChartBar },
  { key: "customers", label: "Customers", icon: Users },
];

export default function RoundRunner() {
  const [tab, setTab] = useState("round");
  const [stops, setStops] = useState(ROUND_SEED);
  const [activeStop, setActiveStop] = useState(null);
  const [provider, setProvider] = useState("SumUp");
  const [weatherBump, setWeatherBump] = useState(false);
  const navigate = useNavigate();

  const stats = useMemo(() => {
    const done = stops.filter((s) => s.status === "done");
    const earned = done.reduce((t, s) => t + (s.paid ? s.price : 0), 0);
    const outstanding = done.reduce((t, s) => t + (!s.paid ? s.price : 0), 0);
    const roundTotal = stops.reduce((t, s) => t + s.price, 0);
    return { done: done.length, total: stops.length, earned, outstanding, roundTotal };
  }, [stops]);

  const openStop = (s) => setActiveStop(s);
  const closeStop = () => setActiveStop(null);

  const updateStop = (id, patch) => {
    setStops((ss) => ss.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    setActiveStop((s) => (s && s.id === id ? { ...s, ...patch } : s));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAGE, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="round-runner-interactive">
      <header className="border-b sticky top-0 z-20" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/round-runner" className="opacity-50" style={{ color: INK }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: TEAL }}>
              <Leaf size={16} weight="fill" color="white" />
            </div>
            <div>
              <div className="text-base font-bold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>Harker &amp; Son <span className="font-mono text-[10px] opacity-60 font-normal">· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">Thursday · round of 5</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=round-runner")} className="text-xs font-semibold px-4 py-2 border-2" style={{ borderColor: INK, color: INK }} data-testid="round-book-cta">
            Talk to us about this
          </button>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: RULE }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} className="relative py-3 text-sm font-medium flex items-center gap-1.5 whitespace-nowrap" style={{ color: tab === t.key ? INK : INK_SOFT }} data-testid={`round-tab-${t.key}`}>
                <Icon size={14} weight="fill" /> {t.label}
                {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: TEAL }} />}
              </button>
            );
          })}
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-8">
        {tab === "round" && <RoundTab stops={stops} stats={stats} openStop={openStop} weatherBump={weatherBump} setWeatherBump={setWeatherBump} />}
        {tab === "week" && <WeekTab />}
        {tab === "customers" && <CustomersTab />}
      </section>

      <footer className="border-t mt-8 py-10" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Concept · the round, on a phone</p>
          <p className="max-w-md mx-auto text-sm opacity-80 mb-5">Pairs with the card reader you already own — {PROVIDERS.join(" / ")} — so Thursday's round pays itself in by lunchtime.</p>
          <Link to="/consultation?demo=round-runner" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: TEAL }}>Book a 30-min consultation <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>

      {activeStop && <JobCardModal stop={activeStop} onClose={closeStop} updateStop={updateStop} provider={provider} setProvider={setProvider} />}
    </div>
  );
}

function RoundTab({ stops, stats, openStop, weatherBump, setWeatherBump }) {
  const pct = Math.round((stats.done / stats.total) * 100);
  return (
    <>
      {/* Header with live stats — modern SaaS vibe */}
      <div className="mb-6">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Today · Thu 24 Apr · 07:40</p>
            <h1 className="text-5xl font-black tracking-tighter leading-[0.9]" style={{ fontFamily: "'Outfit', sans-serif" }}>Thursday round</h1>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Round" value={`${stats.done}/${stats.total}`} sub={`${pct}% done`} accent={TEAL} />
          <StatTile label="Earned" value={`£${stats.earned}`} sub="in the bank" accent={LIME} />
          <StatTile label="Outstanding" value={`£${stats.outstanding}`} sub={stats.outstanding === 0 ? "All clear" : "awaiting"} accent={stats.outstanding > 0 ? CORAL : INK_SOFT} />
          <StatTile label="Round total" value={`£${stats.roundTotal}`} sub="if all pay today" accent={INK} />
        </div>
      </div>

      {/* Weather bump */}
      <div className="mb-6 p-4 flex flex-wrap items-center gap-3 justify-between" style={{ backgroundColor: "#FEF3F2", border: `1px solid ${CORAL}33` }} data-testid="round-weather-panel">
        <div className="flex items-center gap-2.5 text-sm">
          <Cloud size={18} weight="fill" style={{ color: CORAL }} />
          <span>Rain forecast until 10:00. <b>3 window-cleans</b> in today's round.</span>
        </div>
        <button onClick={() => { setWeatherBump(true); toast.success("Window stops bumped to next Thursday · 3 customers notified by SMS"); }} className="text-xs font-semibold px-3 py-2 text-white" style={{ backgroundColor: weatherBump ? INK_SOFT : CORAL }} disabled={weatherBump} data-testid="round-weather-bump">
          {weatherBump ? <><Sun size={12} weight="fill" className="inline mr-1" /> Bumped to next Thu</> : "Bump windows to next Thu"}
        </button>
      </div>

      {/* Discoverability hint */}
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2" data-testid="round-stops-hint">
        Tap any stop · job card · photos · gate code · tap-to-pay
      </p>

      <div className="space-y-2" data-testid="round-stops">
        {stops.map((s, i) => (
          <button key={s.id} onClick={() => openStop(s)} className="group w-full text-left bg-white border p-4 flex items-center gap-4 transition-all hover:border-[color:var(--hoverc)] hover:shadow-[2px_2px_0_0_rgba(15,25,30,0.08)]" style={{ borderColor: s.status === "done" ? TEAL : RULE, "--hoverc": TEAL }} data-testid={`round-stop-${s.id}`}>
            <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: s.status === "done" ? TEAL : PAGE, color: s.status === "done" ? "white" : INK, border: s.status === "done" ? "none" : `2px solid ${RULE}` }}>
              {s.status === "done" ? <CheckCircle size={18} weight="fill" /> : i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <MapPin size={12} className="opacity-60" />
                <h3 className="font-bold text-base truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.customer}</h3>
                {s.gateCode && <span className="text-[10px] font-mono px-1.5 py-0.5" style={{ backgroundColor: LIME_SOFT, color: TEAL_DEEP }}>gate · {s.gateCode}</span>}
              </div>
              <p className="text-sm opacity-70">{s.service}</p>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="font-black text-xl tracking-tight" style={{ color: s.status === "done" && s.paid ? TEAL : INK, fontFamily: "'Outfit', sans-serif" }}>£{s.price}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                {s.status === "done" ? (s.paid ? <span style={{ color: TEAL }}>· paid ·</span> : "unpaid") : `${s.lastVisitDays}d since`}
              </div>
            </div>
            <CaretRight size={16} weight="bold" className="opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" style={{ color: TEAL }} />
          </button>
        ))}
      </div>
    </>
  );
}

function WeekTab() {
  const maxStops = Math.max(...WEEK_DAYS.map((d) => d.stops));
  const totalEarned = WEEK_DAYS.reduce((t, d) => t + d.earned, 0);
  const totalOutstanding = WEEK_DAYS.reduce((t, d) => t + d.outstanding, 0);
  const totalStops = WEEK_DAYS.reduce((t, d) => t + d.stops, 0);
  const bookedValue = 320 * 4; // Fri + Sat upcoming estimate
  const mtd = 4820;
  const mtdTarget = 5500;
  const avgStop = Math.round(totalEarned / Math.max(1, WEEK_DAYS.filter((d) => !d.upcoming).reduce((t, d) => t + d.stops, 0)));

  return (
    <div data-testid="round-week">
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Week 17 · 22–28 Apr</p>
      <h1 className="text-5xl font-black tracking-tighter mb-8 leading-[0.9]" style={{ fontFamily: "'Outfit', sans-serif" }}>This week</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <StatTile label="Earned so far" value={`£${totalEarned}`} sub={`${totalStops} stops · 4 days in`} accent={LIME} big />
        <StatTile label="Outstanding" value={`£${totalOutstanding}`} sub={totalOutstanding === 0 ? "All clear" : "Tue · Mr Brook"} accent={totalOutstanding > 0 ? CORAL : INK_SOFT} big />
        <StatTile label="Booked ahead" value={`£${bookedValue}`} sub="Fri + Sat" accent={TEAL} big />
      </div>

      {/* Week bar chart */}
      <div className="bg-white border p-5 mb-6" style={{ borderColor: RULE }} data-testid="round-week-chart">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Stops per day</h3>
          <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">avg £{avgStop} / stop</span>
        </div>
        <div className="grid grid-cols-6 gap-3 items-end" style={{ height: 160 }}>
          {WEEK_DAYS.map((d) => {
            const h = (d.stops / maxStops) * 100;
            const color = d.upcoming ? "#B8C4CF" : d.today ? TEAL : TEAL_DEEP;
            return (
              <div key={d.day} className="flex flex-col items-center gap-2 h-full justify-end">
                <div className="text-[10px] font-mono opacity-70">{d.earned > 0 ? `£${d.earned}` : "—"}</div>
                <div className="w-full relative" style={{ height: `${h}%`, minHeight: 8 }}>
                  <div className="absolute inset-0" style={{ backgroundColor: color, opacity: d.upcoming ? 0.6 : 1 }} />
                  {d.today && <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ backgroundColor: LIME, boxShadow: `0 0 0 3px ${LIME_SOFT}` }} />}
                </div>
                <div className="text-xs font-bold" style={{ color: d.today ? TEAL : INK, fontFamily: "'Outfit', sans-serif" }}>{d.day}</div>
                <div className="text-[10px] font-mono opacity-60">{d.stops} stop{d.stops === 1 ? "" : "s"}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* MTD progress */}
        <div className="bg-white border p-5" style={{ borderColor: RULE }} data-testid="round-mtd">
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">Month to date</p>
            <span className="text-[10px] font-mono opacity-60">vs. last month</span>
          </div>
          <div className="flex items-baseline gap-3 mb-1">
            <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>£{mtd.toLocaleString()}</div>
            <div className="flex items-center gap-1 text-xs font-bold" style={{ color: LIME }}>
              <TrendUp size={14} weight="bold" /> +12%
            </div>
          </div>
          <div className="h-1.5 mt-3 mb-1 overflow-hidden" style={{ backgroundColor: "#F0F2F5" }}>
            <div className="h-full" style={{ width: `${Math.round((mtd / mtdTarget) * 100)}%`, backgroundColor: TEAL }} />
          </div>
          <div className="flex items-baseline justify-between text-[10px] font-mono opacity-60">
            <span>{Math.round((mtd / mtdTarget) * 100)}% of £{mtdTarget.toLocaleString()} target</span>
            <span>£{(mtdTarget - mtd).toLocaleString()} to go</span>
          </div>
        </div>

        {/* Payment health */}
        <div className="bg-white border p-5" style={{ borderColor: RULE }} data-testid="round-health">
          <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-3">Payment health</p>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-4xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: TEAL }}>92%</div>
            <span className="text-xs opacity-60">paid within 48h</span>
          </div>
          <div className="space-y-1.5 text-xs">
            <HealthRow label="Paid on the day" value="68%" barPct={68} color={LIME} />
            <HealthRow label="Within 48h" value="24%" barPct={24} color={TEAL} />
            <HealthRow label="Chased / overdue" value="8%" barPct={8} color={CORAL} />
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomersTab() {
  const totalOutstanding = CUSTOMERS_SEED.reduce((t, c) => t + c.outstanding, 0);
  const totalLTV = CUSTOMERS_SEED.reduce((t, c) => t + c.ltv, 0);
  return (
    <div data-testid="round-customers">
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Ledger · 12 active · 5 shown</p>
      <h1 className="text-5xl font-black tracking-tighter mb-8 leading-[0.9]" style={{ fontFamily: "'Outfit', sans-serif" }}>Customers</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <StatTile label="Lifetime value" value={`£${totalLTV.toLocaleString()}`} sub="across 5 shown" accent={TEAL} big />
        <StatTile label="Outstanding" value={`£${totalOutstanding}`} sub={totalOutstanding > 0 ? "2 customers" : "All clear"} accent={totalOutstanding > 0 ? CORAL : INK_SOFT} big />
        <StatTile label="Cadence" value="Weekly" sub="avg visit gap 14d" accent={INK} big />
      </div>

      <div className="bg-white border" style={{ borderColor: RULE }}>
        <div className="grid grid-cols-[1fr_90px_90px_90px_60px] gap-4 px-5 py-3 border-b text-[10px] font-mono uppercase tracking-widest opacity-60" style={{ borderColor: RULE }}>
          <div>Customer</div>
          <div>Cadence</div>
          <div>Visits · LTV</div>
          <div className="text-right">Outstanding</div>
          <div className="text-right">Last paid</div>
        </div>
        {CUSTOMERS_SEED.map((c) => (
          <div key={c.customer} className="grid grid-cols-[1fr_90px_90px_90px_60px] gap-4 px-5 py-4 border-b last:border-b-0 items-center" style={{ borderColor: RULE }} data-testid={`round-customer-${c.customer.replace(/\s+/g, '-').toLowerCase()}`}>
            <div>
              <div className="font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.customer}</div>
              <div className="text-xs opacity-60">{c.name}</div>
            </div>
            <div className="text-xs font-mono">{c.cadence}</div>
            <div className="text-xs font-mono"><b>{c.visits}</b> · £{c.ltv.toLocaleString()}</div>
            <div className="text-right">
              {c.outstanding > 0 ? (
                <span className="inline-flex items-center gap-1 font-bold text-sm" style={{ color: CORAL }}>
                  <WarningCircle size={12} weight="fill" /> £{c.outstanding}
                </span>
              ) : (
                <span className="text-[10px] font-mono opacity-50">—</span>
              )}
            </div>
            <div className="text-right text-[10px] font-mono opacity-70">{c.lastPaid}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, accent, big }) {
  return (
    <div className="bg-white border p-4" style={{ borderColor: RULE }}>
      <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">{label}</p>
      <div className={`${big ? "text-4xl" : "text-3xl"} font-black mt-1 tracking-tight leading-none`} style={{ color: accent, fontFamily: "'Outfit', sans-serif" }}>{value}</div>
      {sub && <p className="text-[11px] opacity-60 mt-1">{sub}</p>}
    </div>
  );
}

function HealthRow({ label, value, barPct, color }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-0.5">
        <span className="opacity-70">{label}</span>
        <span className="font-mono font-bold">{value}</span>
      </div>
      <div className="h-1 overflow-hidden" style={{ backgroundColor: "#F0F2F5" }}>
        <div className="h-full" style={{ width: `${barPct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function JobCardModal({ stop, onClose, updateStop, provider, setProvider }) {
  const [step, setStep] = useState(stop.status === "done" ? "paid" : stop.photoBefore ? (stop.photoAfter ? "invoice" : "after") : "before");

  const snapBefore = () => { updateStop(stop.id, { photoBefore: "snapped" }); setStep("after"); toast.success("Before photo captured"); };
  const snapAfter = () => { updateStop(stop.id, { photoAfter: "snapped" }); setStep("invoice"); toast.success("After photo captured"); };
  const sendInvoice = () => { toast.success(`Invoice sent via ${provider} · £${stop.price}`); setStep("paid-await"); };
  const markPaid = () => { updateStop(stop.id, { status: "done", paid: true }); toast.success("Payment received"); setStep("paid"); };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-xl max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="round-job-modal">
        <div className="px-5 py-4 border-b flex items-start justify-between gap-4" style={{ borderColor: RULE, backgroundColor: PAGE_LIGHT }}>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Job card</p>
            <h2 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{stop.customer}</h2>
            <p className="text-sm opacity-70">{stop.name}</p>
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="round-modal-close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <InfoRow label="Service" value={stop.service} />
          <InfoRow label="Price" value={<span className="font-bold" style={{ color: TEAL }}>£{stop.price}</span>} />
          <InfoRow label="Gate code" value={stop.gateCode ? <span className="font-mono px-2 py-0.5 text-sm" style={{ backgroundColor: LIME_SOFT, color: TEAL_DEEP }}>{stop.gateCode}</span> : <span className="opacity-50 text-sm">—</span>} />
          <InfoRow label="Note" value={<span className="text-sm italic opacity-80">{stop.note}</span>} />
          <InfoRow label="Last visit" value={<span className="text-sm opacity-70">{stop.lastVisitDays} days ago</span>} />
        </div>

        {stop.status !== "done" && (
          <div className="px-5 pb-5 space-y-3">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest">
              <StepDot done={step !== "before"} label="Before" />
              <div className="flex-1 h-px" style={{ backgroundColor: step !== "before" ? TEAL : RULE }} />
              <StepDot done={["invoice", "paid-await", "paid"].includes(step)} label="After" />
              <div className="flex-1 h-px" style={{ backgroundColor: ["invoice", "paid-await", "paid"].includes(step) ? TEAL : RULE }} />
              <StepDot done={["paid-await", "paid"].includes(step)} label="Invoice" />
              <div className="flex-1 h-px" style={{ backgroundColor: step === "paid" ? TEAL : RULE }} />
              <StepDot done={step === "paid"} label="Paid" />
            </div>

            {step === "before" && (
              <button onClick={snapBefore} className="w-full py-4 font-semibold text-white flex items-center justify-center gap-2" style={{ backgroundColor: TEAL }} data-testid="round-snap-before"><Camera size={18} weight="fill" /> Snap before photo</button>
            )}
            {step === "after" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="aspect-square flex items-center justify-center" style={{ backgroundColor: LIME_SOFT, border: `2px solid ${TEAL}` }}><Camera size={28} weight="duotone" style={{ color: TEAL }} /></div>
                <button onClick={snapAfter} className="aspect-square font-semibold text-white flex flex-col items-center justify-center gap-2" style={{ backgroundColor: TEAL }} data-testid="round-snap-after"><Camera size={24} weight="fill" /> Snap after</button>
              </div>
            )}
            {step === "invoice" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="aspect-square flex items-center justify-center" style={{ backgroundColor: LIME_SOFT, border: `2px solid ${TEAL}` }}><Camera size={28} weight="duotone" style={{ color: TEAL }} /></div>
                  <div className="aspect-square flex items-center justify-center" style={{ backgroundColor: LIME_SOFT, border: `2px solid ${TEAL}` }}><CheckCircle size={28} weight="fill" style={{ color: TEAL }} /></div>
                </div>
                <div className="p-4 border" style={{ borderColor: RULE, backgroundColor: PAGE }}>
                  <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">Request payment via</p>
                  <div className="flex gap-2 mb-3">
                    {PROVIDERS.map((p) => (
                      <button key={p} onClick={() => setProvider(p)} className="flex-1 py-2 text-xs font-semibold border-2" style={{ borderColor: provider === p ? TEAL : RULE, backgroundColor: provider === p ? TEAL : "white", color: provider === p ? "white" : INK }} data-testid={`round-provider-${p.toLowerCase()}`}>{p}</button>
                    ))}
                  </div>
                  <button onClick={sendInvoice} className="w-full py-3 font-semibold text-white flex items-center justify-center gap-2" style={{ backgroundColor: INK }} data-testid="round-send-invoice">
                    <CreditCard size={16} weight="fill" /> Send invoice + pay link · £{stop.price}
                  </button>
                </div>
              </div>
            )}
            {step === "paid-await" && (
              <div className="p-5 text-center space-y-3" style={{ backgroundColor: LIME_SOFT, border: `1px dashed ${TEAL}` }}>
                <Clock size={32} weight="duotone" className="mx-auto" style={{ color: TEAL }} />
                <p className="text-sm">Customer has the pay-link. Tap below when payment lands.</p>
                <button onClick={markPaid} className="w-full py-3 font-semibold text-white" style={{ backgroundColor: TEAL }} data-testid="round-mark-paid">Mark as paid</button>
              </div>
            )}
          </div>
        )}

        {step === "paid" && (
          <div className="mx-5 mb-5 p-5 text-center" style={{ backgroundColor: TEAL, color: "white" }}>
            <CheckCircle size={32} weight="fill" className="mx-auto mb-2" />
            <p className="font-bold">Paid · £{stop.price} in the bank</p>
            <p className="text-xs opacity-80 mt-1">Invoice with before/after photos emailed to {stop.name}.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-4">
      <div className="w-24 text-[10px] font-mono uppercase tracking-widest opacity-60">{label}</div>
      <div className="flex-1">{value}</div>
    </div>
  );
}

function StepDot({ done, label }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: done ? TEAL : RULE }} />
      <span className="opacity-60 text-[9px]">{label}</span>
    </div>
  );
}
